import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef, GridGroupingColDefOverride } from "@mui/x-data-grid-premium";
import { useGridApiRef, useKeepGroupedColumnsHidden } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import CompletionPercentageIndicator from "../../common/CompletionPercentageIndicator";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { formatDurationInSeconds } from "@entrilia/shared/utilities/formatters";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import type {
  InvestorCommunicationReader,
  InvestorCommunicationRecipient,
  InvestorCommunicationType,
} from "../../../api/types/investorCommunicationsTypes";
import useClientContext from "../../../context/useClientContext";
import { pageRoutes } from "../../../routes";
import { investorCommunicationEngagementLabels } from "./communication-details/summary/engagement/investorCommunicationEngagementLabels";

const ContactCell = ({ row }: { row: InvestorCommunicationRecipient }) => {
  const { clientCode } = useClientContext();

  return (
    <GridTextHoverLink
      to={`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.contacts}/${row.contactId}/main`}
      openInNewTab
    >
      <Stack spacing={0.5} sx={{ pl: 5 }}>
        <Typography variant="subtitle2" noWrap>
          {row.contactName}
        </Typography>
        <Typography variant="caption" noWrap color="textSecondary">
          {row.contactEmail}
        </Typography>
      </Stack>
    </GridTextHoverLink>
  );
};

interface PortalCellProps {
  type: InvestorCommunicationType;
  reader?: InvestorCommunicationReader;
}

const PortalCell = ({ type, reader }: PortalCellProps) => {
  if (reader === undefined) {
    return <Typography color="textSecondary">Not viewed</Typography>;
  }

  const views = `${reader.viewsCount} ${pluralize("view", reader.viewsCount)}`;
  const lastViewed = convertISODateTime(reader.lastViewedAt);
  const readTime = formatDurationInSeconds(reader.totalReadTimeSeconds);
  const { viewedLabel, timeSpentLabel } = investorCommunicationEngagementLabels[type];

  return (
    <Tooltip arrow title={`${views} · Last viewed ${lastViewed} · ${readTime} ${timeSpentLabel}`}>
      <Box>
        <CompletionPercentageIndicator value={reader.completionPercentage} label={viewedLabel} />
      </Box>
    </Tooltip>
  );
};

const groupingColumnDefinition: GridGroupingColDefOverride<InvestorCommunicationRecipient> = {
  headerName: "Name",
  leafField: "contactName",
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  flex: 3,
};

const baseColumnDefinitions: GridColDef<InvestorCommunicationRecipient>[] = [
  {
    field: "investorName",
    headerName: "",
    renderCell: ({ rowNode, field, value }) => {
      if (rowNode.type === "group" && field === rowNode.groupingField) {
        return "";
      }

      return (
        <Typography variant="subtitle2" noWrap>
          {value}
        </Typography>
      );
    },
  },
  {
    field: "contactName",
    headerName: "Name",
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return "";
      }

      return <ContactCell row={row} />;
    },
  },
];

// TODO: add the Email Status column in the email-notifications iteration.
const getColumnDefinitions = (
  type: InvestorCommunicationType,
  readersByContactId: Map<string, InvestorCommunicationReader> | undefined
): GridColDef<InvestorCommunicationRecipient>[] =>
  readersByContactId === undefined
    ? baseColumnDefinitions
    : [
        ...baseColumnDefinitions,
        {
          field: "portal",
          headerName: "Portal",
          width: 180,
          renderCell: ({ row, rowNode }) =>
            rowNode.type === "group" ? "" : <PortalCell type={type} reader={readersByContactId.get(row.contactId)} />,
        },
      ];

const getRowId = (row: InvestorCommunicationRecipient) => `${row.investorId}_${row.contactId}`;

interface Props {
  type: InvestorCommunicationType;
  recipients: InvestorCommunicationRecipient[];
  readersByContactId?: Map<string, InvestorCommunicationReader>;
}

const InvestorCommunicationRecipientsGrid = ({ type, recipients, readersByContactId }: Props) => {
  const gridApiRef = useGridApiRef();

  const columnDefinitions = useMemo(() => getColumnDefinitions(type, readersByContactId), [type, readersByContactId]);

  const { isGroupExpanded } = useGridGroupsExpanded(gridApiRef);

  const gridInitialState = useKeepGroupedColumnsHidden({
    apiRef: gridApiRef,
    initialState: {
      rowGrouping: {
        model: ["investorName"],
      },
      columns: {
        columnVisibilityModel: {
          contactName: false,
        },
      },
    },
  });

  return (
    <DataGrid<InvestorCommunicationRecipient>
      apiRef={gridApiRef}
      columns={columnDefinitions}
      groupingColDef={groupingColumnDefinition}
      rows={recipients}
      getRowId={getRowId}
      multilineRows
      initialState={gridInitialState}
      isGroupExpandedByDefault={isGroupExpanded}
      noRowsText="No recipients"
      disableColumnPinning
      disableColumnSorting
      disableColumnReorder
      disableMultipleRowSelection
      containerSx={{ minHeight: 320 }}
    />
  );
};

export default InvestorCommunicationRecipientsGrid;
